import React, { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode } from 'react';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// ============================================
// SHARED INTERFACES (lifted from WellConfig)
// ============================================

export interface SurveyPoint {
  survey: string | number;
  md: number;
  inc: number;
  azimuth: number;
  tvd: number;
  vs: number;
  ns: number;
  ew: number;
  dls: number;
  source: string | null;
}

export interface SurveyData {
  header: {
    well: string;
    api: string;
    reportDate: string;
    latitude: number;
    longitude: number;
    surveyCalc: string;
    magReference: string;
  };
  surveys: SurveyPoint[];
}

export interface PerforationStage {
  stage: number;
  topPerf: number;
  bottomPerf: number;
  clusters: number;
  diameter: number;
  holes: number;
}

export interface TreatmentStep {
  id: number;
  step: number;
  type: string;
  description: string;
  rate: number;
  conc: number;
  clean_step: number;
  slurry_step: number;
  sand_step: number;
  time_step: number;
  start_conc?: number;
  end_conc?: number;
}

export interface CasingString {
  id: string;
  type: 'casing' | 'tubing';
  pipeOD: number;
  pipeID: number;
  weight: number;
  grade: string;
  topDepth: number;
  bottomDepth: number;
  capacityFactor: number;
  effectiveLength: number;
  volume: number;
}

export interface WellConfigData {
  file: File | null;
  uploadedAt: Date | null;
  casingStrings: CasingString[];
  perforations: PerforationStage[];
  treatment: TreatmentStep[];
}

export interface UploadedSurvey {
  file: File;
  data: SurveyData;
  uploadedAt: Date;
}

// ============================================
// CORE WORKFLOW TYPES
// ============================================

export interface WellData {
  id: string;
  name: string;
  api: string;
  afe: string;
  stages: number;
  tvd: number;
  td: number;
  curveStart: number;
  curveEnd: number;
  maxPsi: number;
  status: 'Active' | 'Planned' | 'Completed';
}

export interface PadData {
  company: string;
  organizationId: number;
  padName: string;
  state: string;
  county: string;
  fleet: number;
}

export interface ScheduleData {
  startDate: Date | null;
  endDate: Date | null;
  fleetId: string;
  district: string;
  expectedStagesPerDay: number;
  operator: string;
}

export interface WorkflowState {
  currentStep: number;
  padId: number | null;
  padData: PadData;
  wells: WellData[];
  schedule: ScheduleData;
  isValid: boolean;
  uploadedFile: File | null;
  isLoading: boolean;
  // Well sub-data — kept in main state so loadPadFromApi is one atomic setState
  wellConfigs: Record<string, WellConfigData>;
  uploadedSurveys: Record<string, UploadedSurvey>;
  // DB wells whose survey was removed in the UI — DELETE issued on deploy
  deletedSurveyIds: string[];
}

export interface DeployProgress {
  status: 'idle' | 'deploying' | 'success' | 'error';
  currentStep: string;
  completedSteps: string[];
  error: string | null;
}

interface WorkflowContextValue {
  state: WorkflowState;
  setCurrentStep: (step: number) => void;
  updatePadData: (data: Partial<PadData>) => void;
  setWells: (wells: WellData[]) => void;
  addWell: (well: WellData) => void;
  updateWell: (id: string, data: Partial<WellData>) => void;
  removeWell: (id: string) => void;
  updateSchedule: (data: Partial<ScheduleData>) => void;
  setUploadedFile: (file: File | null) => void;
  resetWorkflow: () => void;
  validateStep: (step: number) => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  loadPadFromApi: (padId: number) => Promise<void>;
  // Typed well-config action creators (replaces raw setWellConfigs/setUploadedSurveys)
  updateWellConfig: (wellId: string, updater: (prev: WellConfigData | undefined) => WellConfigData | null) => void;
  setUploadedSurvey: (wellId: string, survey: UploadedSurvey | null) => void;
  // Deploy
  deployWorkflow: () => Promise<{ success: boolean; error?: string }>;
  deployProgress: DeployProgress;
}

const initialPadData: PadData = {
  company: '',
  organizationId: 0,
  padName: '',
  state: '',
  county: '',
  fleet: 0,
};

const initialSchedule: ScheduleData = {
  startDate: null,
  endDate: null,
  fleetId: '',
  district: '',
  expectedStagesPerDay: 6,
  operator: '',
};

const initialState: WorkflowState = {
  currentStep: 0,
  padId: null,
  padData: initialPadData,
  wells: [],
  schedule: initialSchedule,
  isValid: false,
  uploadedFile: null,
  isLoading: false,
  wellConfigs: {},
  uploadedSurveys: {},
  deletedSurveyIds: [],
};

const WorkflowContext = createContext<WorkflowContextValue | undefined>(undefined);

interface WorkflowProviderProps {
  children: ReactNode;
  onComplete?: (state: WorkflowState) => void;
}

// ============================================
// HELPERS
// ============================================

/** Module-level org cache shared with Pads.tsx session */
let ORG_CACHE_CTX: Array<{ id: number; name: string }> | null = null;

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function getAuthHeadersMultipart(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/** True if the well ID is a real database ID (numeric), not a local temp ID */
function isPersistedWell(wellId: string): boolean {
  return /^\d+$/.test(wellId);
}

/** Pure helper — mirrors the same function in WellConfig */
function calculateCasingVolumes(casings: CasingString[]): CasingString[] {
  if (casings.length === 0) return [];
  const sorted = [...casings].sort((a, b) => a.topDepth - b.topDepth);
  return sorted.map((casing, i) => {
    const nextTop = i < sorted.length - 1 ? sorted[i + 1].topDepth : null;
    const effectiveBottom = nextTop !== null && nextTop < casing.bottomDepth
      ? nextTop
      : casing.bottomDepth;
    const effectiveLength = Math.max(0, effectiveBottom - casing.topDepth);
    const volume = effectiveLength * casing.capacityFactor;
    return { ...casing, effectiveLength, volume };
  });
}

/** Load per-well sub-data (casing, perfs, treatment, survey) for a single well */
async function loadWellSubData(
  well: WellData,
  headers: Record<string, string>
): Promise<{
  wellId: string;
  casingStrings: CasingString[];
  perforations: PerforationStage[];
  treatment: TreatmentStep[];
  surveyData: SurveyData | null;
}> {
  const wellId = well.id;

  const [casingRes, perfsRes, stagesRes, surveyRes] = await Promise.all([
    fetch(`${backendUrl}/wells/${wellId}/casing-strings`, { headers }).catch(() => null),
    fetch(`${backendUrl}/wells/${wellId}/perforations`, { headers }).catch(() => null),
    fetch(`${backendUrl}/wells/${wellId}/stages`, { headers }).catch(() => null),
    fetch(`${backendUrl}/wells/${wellId}/survey`, { headers }).catch(() => null),
  ]);

  // Casing strings
  let casingStrings: CasingString[] = [];
  if (casingRes?.ok) {
    const raw = await casingRes.json();
    casingStrings = (raw || []).map((c: any, i: number) => ({
      id: `cs-api-${c.id || i}`,
      type: (c.pipe_type || 'casing') as 'casing' | 'tubing',
      pipeOD: c.pipe_od || 0,
      pipeID: c.pipe_id || 0,
      weight: c.weight || 0,
      grade: c.grade || '',
      topDepth: c.top_depth || 0,
      bottomDepth: c.bottom_depth || 0,
      capacityFactor: c.capacity_factor || 0,
      effectiveLength: c.effective_length || 0,
      volume: c.volume || 0,
    }));
    if (casingStrings.length > 0) {
      casingStrings = calculateCasingVolumes(casingStrings);
    }
  }

  // Perforations
  let perforations: PerforationStage[] = [];
  if (perfsRes?.ok) {
    const raw = await perfsRes.json();
    perforations = (raw || [])
      .filter((s: any) => s.clusters && s.clusters.length > 0)
      .flatMap((s: any) =>
        s.clusters.map((c: any) => ({
          stage: s.stage_number,
          clusters: c.cluster_number,
          topPerf: c.top_depth_ft || 0,
          bottomPerf: c.bottom_depth_ft || 0,
          diameter: c.diameter_in || 0,
          holes: c.holes || 0,
        }))
      )
      .sort((a: PerforationStage, b: PerforationStage) =>
        a.stage !== b.stage ? a.stage - b.stage : a.clusters - b.clusters
      );
  }

  // Treatment (from first stage's latest design)
  let treatment: TreatmentStep[] = [];
  if (stagesRes?.ok) {
    const stages = await stagesRes.json();
    if (stages.length > 0) {
      const firstStageId = stages[0].id;
      try {
        const treatRes = await fetch(
          `${backendUrl}/stages/${firstStageId}/design_treatment_schedule/latest`,
          { headers }
        );
        if (treatRes.ok) {
          const raw = await treatRes.json();
          treatment = (raw || []).map((t: any, i: number) => ({
            id: t.id || i + 1,
            step: t.step || i + 1,
            type: t.type || '',
            description: t.description || '',
            rate: t.rate || 0,
            conc: t.conc || 0,
            clean_step: t.clean_step || 0,
            slurry_step: t.slurry_step || 0,
            sand_step: t.sand_step || 0,
            time_step: t.rate > 0 ? (t.clean_step / t.rate) * 60 : 0,
            start_conc: t.start_conc ?? undefined,
            end_conc: t.end_conc ?? undefined,
          }));
        }
      } catch (err) {
        console.error(`Failed to fetch treatment for first stage of well ${wellId}:`, err);
      }
    }
  }

  // Directional survey
  let surveyData: SurveyData | null = null;
  if (surveyRes?.ok) {
    const raw = await surveyRes.json();
    const surveyHeader = Array.isArray(raw) ? {} : (raw.header || {});
    const surveyPoints: any[] = Array.isArray(raw) ? raw : (raw.surveys || []);
    if (surveyPoints.length > 0) {
      surveyData = {
        header: {
          well: well.name,
          api: well.api || '',
          reportDate: '',
          latitude: surveyHeader.latitude || 0,
          longitude: surveyHeader.longitude || 0,
          surveyCalc: surveyHeader.survey_calc || '',
          magReference: '',
        },
        surveys: surveyPoints.map((s: any, i: number) => ({
          survey: s.survey_number === 0 || i === 0 ? 'Tie In' : s.survey_number || i,
          md: s.md_ft || 0,
          inc: s.inc_deg || 0,
          azimuth: s.azi_deg || 0,
          tvd: s.tvd_ft || 0,
          vs: 0,
          ns: s.grid_north_ft || 0,
          ew: s.grid_east_ft || 0,
          dls: s.dls ?? 0,
          source: null,
        })),
      };
    }
  }

  return { wellId, casingStrings, perforations, treatment, surveyData };
}

// ============================================
// PROVIDER
// ============================================

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children, onComplete }) => {
  const [state, setState] = useState<WorkflowState>(initialState);
  const [deployProgress, setDeployProgress] = useState<DeployProgress>({
    status: 'idle',
    currentStep: '',
    completedSteps: [],
    error: null,
  });

  // Always-current ref — lets callbacks read latest state without being in deps.
  // Pattern: assign on every render, read inside stable useCallback(() => ..., []).
  const stateRef = useRef(state);
  stateRef.current = state;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const setCurrentStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const updatePadData = useCallback((data: Partial<PadData>) => {
    setState(prev => ({ ...prev, padData: { ...prev.padData, ...data } }));
  }, []);

  const setWells = useCallback((wells: WellData[]) => {
    setState(prev => ({ ...prev, wells }));
  }, []);

  const addWell = useCallback((well: WellData) => {
    setState(prev => ({ ...prev, wells: [...prev.wells, well] }));
  }, []);

  const updateWell = useCallback((id: string, data: Partial<WellData>) => {
    setState(prev => ({
      ...prev,
      wells: prev.wells.map(w => (w.id === id ? { ...w, ...data } : w)),
    }));
  }, []);

  const removeWell = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      wells: prev.wells.filter(w => w.id !== id),
      wellConfigs: Object.fromEntries(Object.entries(prev.wellConfigs).filter(([k]) => k !== id)),
      uploadedSurveys: Object.fromEntries(Object.entries(prev.uploadedSurveys).filter(([k]) => k !== id)),
    }));
  }, []);

  const updateSchedule = useCallback((data: Partial<ScheduleData>) => {
    setState(prev => ({ ...prev, schedule: { ...prev.schedule, ...data } }));
  }, []);

  const setUploadedFile = useCallback((file: File | null) => {
    setState(prev => ({ ...prev, uploadedFile: file }));
  }, []);

  const resetWorkflow = useCallback(() => {
    setState(initialState);
    setDeployProgress({ status: 'idle', currentStep: '', completedSteps: [], error: null });
  }, []);

  // Typed well-config action creators — callers never touch setState directly.
  const updateWellConfig = useCallback((
    wellId: string,
    updater: (prev: WellConfigData | undefined) => WellConfigData | null
  ) => {
    setState(prev => {
      const result = updater(prev.wellConfigs[wellId]);
      if (result === null) {
        const next = { ...prev.wellConfigs };
        delete next[wellId];
        return { ...prev, wellConfigs: next };
      }
      return { ...prev, wellConfigs: { ...prev.wellConfigs, [wellId]: result } };
    });
  }, []);

  const setUploadedSurvey = useCallback((wellId: string, survey: UploadedSurvey | null) => {
    setState(prev => {
      if (survey === null) {
        const next = { ...prev.uploadedSurveys };
        delete next[wellId];
        // Track DB wells for survey deletion on deploy (numeric ID = persisted in DB)
        const isDbWell = /^\d+$/.test(wellId);
        const alreadyTracked = prev.deletedSurveyIds.includes(wellId);
        return {
          ...prev,
          uploadedSurveys: next,
          deletedSurveyIds: isDbWell && !alreadyTracked
            ? [...prev.deletedSurveyIds, wellId]
            : prev.deletedSurveyIds,
        };
      }
      // Re-uploading a survey — remove from deletion list if it was queued
      return {
        ...prev,
        uploadedSurveys: { ...prev.uploadedSurveys, [wellId]: survey },
        deletedSurveyIds: prev.deletedSurveyIds.filter(id => id !== wellId),
      };
    });
  }, []);

  // ── Load pad + wells from API ──────────────────────────────────
  const loadPadFromApi = useCallback(async (padId: number) => {
    setState(prev => ({ ...prev, isLoading: true }));
    const headers = getAuthHeaders();

    try {
      const padRes = await fetch(`${backendUrl}/pads/${padId}`, { headers });
      if (!padRes.ok) throw new Error('Failed to fetch pad');
      const pad = await padRes.json();

      let companyName = '';
      if (pad.organization_id) {
        try {
          if (!ORG_CACHE_CTX) {
            const orgsRes = await fetch(`${backendUrl}/organizations/list`, { headers });
            if (orgsRes.ok) ORG_CACHE_CTX = await orgsRes.json();
          }
          const org = (ORG_CACHE_CTX || []).find((o: any) => o.id === pad.organization_id);
          companyName = org?.name || '';
        } catch { /* non-critical */ }
      }

      const wellsRes = await fetch(`${backendUrl}/pads/${padId}/wells`, { headers });
      const wellsRaw = wellsRes.ok ? await wellsRes.json() : [];

      const mappedWells: WellData[] = wellsRaw.map((w: any) => ({
        id: String(w.id),
        name: w.well_name || '',
        api: w.api || '',
        afe: w.afe || '',
        stages: w.stage_count || 0,
        tvd: w.tvd || 0,
        td: w.td || 0,
        curveStart: w.curve_start || 0,
        curveEnd: w.curve_end || 0,
        maxPsi: w.max_psi || 0,
        status: 'Planned' as const,
      }));

      const scheduleUpdate: Partial<ScheduleData> = {};
      // ── Use customer_start_ts / customer_end_ts for workflow dates ──
      // start_ts / end_ts are owned by the worker (actual frac timestamps)
      // and must not be used for the planning workflow.
      if (pad.customer_start_ts) scheduleUpdate.startDate = new Date(pad.customer_start_ts);
      if (pad.customer_end_ts) scheduleUpdate.endDate = new Date(pad.customer_end_ts);
      if (pad.expected_stages_per_day != null) scheduleUpdate.expectedStagesPerDay = pad.expected_stages_per_day;
      if (pad.fleet) scheduleUpdate.fleetId = String(pad.fleet);
      scheduleUpdate.operator = companyName;

      // Load all well sub-data in parallel before clearing isLoading.
      // Everything lands in ONE setState so the loading screen never shows
      // a partially-populated UI.
      const subDataResults = await Promise.all(
        mappedWells.map(w => loadWellSubData(w, headers).catch(err => {
          console.error(`Failed to load sub-data for well ${w.name}:`, err);
          return { wellId: w.id, casingStrings: [], perforations: [], treatment: [], surveyData: null };
        }))
      );

      const newWellConfigs: Record<string, WellConfigData> = {};
      const newSurveys: Record<string, UploadedSurvey> = {};
      for (const { wellId, casingStrings, perforations, treatment, surveyData } of subDataResults) {
        if (casingStrings.length > 0 || perforations.length > 0 || treatment.length > 0) {
          newWellConfigs[wellId] = { file: null, uploadedAt: null, casingStrings, perforations, treatment };
        }
        if (surveyData) {
          newSurveys[wellId] = { file: new File([], 'survey-from-db.xlsx'), data: surveyData, uploadedAt: new Date() };
        }
      }

      // Single atomic setState — isLoading, wells, wellConfigs, surveys all flip together.
      setState(prev => ({
        ...prev,
        padId,
        padData: {
          company: companyName,
          organizationId: pad.organization_id || 0,
          padName: pad.pad_name || '',
          state: pad.state || '',
          county: pad.county || '',
          fleet: pad.fleet || 0,
        },
        wells: mappedWells,
        schedule: { ...prev.schedule, ...scheduleUpdate },
        wellConfigs: newWellConfigs,
        uploadedSurveys: newSurveys,
        isLoading: false,
      }));
    } catch (err) {
      console.error('Failed to load pad:', err);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  // ══════════════════════════════════════════════════════════════
  // DEPLOY WORKFLOW
  // ══════════════════════════════════════════════════════════════
  const deployWorkflow = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    const headers = getAuthHeaders();
    // Read from ref — no state in deps, callback stays stable forever
    const { padData, wells, schedule, padId, wellConfigs, uploadedSurveys } = stateRef.current;
    const isEdit = padId !== null;

    const progress = (step: string) => {
      setDeployProgress(prev => ({
        ...prev,
        status: 'deploying',
        currentStep: step,
        completedSteps: prev.currentStep ? [...prev.completedSteps, prev.currentStep] : prev.completedSteps,
      }));
    };

    try {
      setDeployProgress({ status: 'deploying', currentStep: 'Saving pad...', completedSteps: [], error: null });

      // ── 1. Create or update pad ──────────────────────────
      let currentPadId = padId;

      if (isEdit && currentPadId) {
        progress('Updating pad...');
        const padRes = await fetch(`${backendUrl}/pads/${currentPadId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            pad_name: padData.padName,
            fleet: padData.fleet || null,
            state: padData.state || null,
            county: padData.county || null,
            status: 'Active',
          }),
        });
        if (!padRes.ok) {
          const err = await padRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to update pad');
        }
      } else {
        progress('Creating pad...');
        const padRes = await fetch(`${backendUrl}/pads`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            pad_name: padData.padName,
            organization_id: padData.organizationId,
            fleet: padData.fleet || null,
            state: padData.state || null,
            county: padData.county || null,
          }),
        });
        if (!padRes.ok) {
          const err = await padRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to create pad');
        }
        const newPad = await padRes.json();
        currentPadId = newPad.id;
        setState(prev => ({ ...prev, padId: currentPadId }));
      }

      // ── 2. Create new wells (those with "well-*" IDs) ────
      // Build a map from old temp ID → new real ID
      const idMap: Record<string, string> = {};

      for (const well of wells) {
        if (!isPersistedWell(well.id)) {
          progress(`Creating well ${well.name}...`);
          const wellRes = await fetch(`${backendUrl}/pads/${currentPadId}/wells`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              well_name: well.name,
              api: well.api || '',
              afe: well.afe || '',
              stage_count: well.stages,
              tvd: well.tvd,
              td: well.td,
              curve_start: well.curveStart,
              curve_end: well.curveEnd,
              max_psi: well.maxPsi,
            }),
          });
          if (!wellRes.ok) {
            const err = await wellRes.json().catch(() => ({}));
            throw new Error(err.error || `Failed to create well ${well.name}`);
          }
          const newWell = await wellRes.json();
          idMap[well.id] = String(newWell.id);
        } else {
          idMap[well.id] = well.id; // Already persisted
        }
      }

      // Update wells in state with real IDs
      setState(prev => ({
        ...prev,
        wells: prev.wells.map(w => ({
          ...w,
          id: idMap[w.id] || w.id,
        })),
      }));

      // Remap wellConfigs and uploadedSurveys to real IDs
      const remappedConfigs: Record<string, WellConfigData> = {};
      const remappedSurveys: Record<string, UploadedSurvey> = {};
      for (const [oldId, config] of Object.entries(stateRef.current.wellConfigs)) {
        remappedConfigs[idMap[oldId] || oldId] = config;
      }
      for (const [oldId, survey] of Object.entries(stateRef.current.uploadedSurveys)) {
        remappedSurveys[idMap[oldId] || oldId] = survey;
      }
      // Persist remapped IDs into state (all in one setState)
      setState(prev => ({ ...prev, wellConfigs: remappedConfigs, uploadedSurveys: remappedSurveys }));

      // ── 3. Deploy config for each well ───────────────────
      for (const well of wells) {
        const realId = idMap[well.id] || well.id;
        const config = remappedConfigs[realId];
        const survey = remappedSurveys[realId];

        // 3a. deploy-config (metadata + stages + perfs + treatment)
        progress(`Deploying ${well.name} config...`);

        // Build perforations payload: group flat cluster rows back by stage
        const perfsByStage = new Map<number, Array<{ cluster_number: number; top_depth_ft: number; bottom_depth_ft: number; diameter_in: number; holes: number }>>();
        for (const p of (config?.perforations || [])) {
          if (!perfsByStage.has(p.stage)) perfsByStage.set(p.stage, []);
          perfsByStage.get(p.stage)!.push({
            cluster_number: p.clusters,    // clusters field = cluster number
            top_depth_ft: p.topPerf,
            bottom_depth_ft: p.bottomPerf,
            diameter_in: p.diameter,
            holes: p.holes,
          });
        }
        const perfsPayload = Array.from(perfsByStage.entries())
          .sort(([a], [b]) => a - b)
          .map(([stageNum, clusters]) => ({
            stage_number: stageNum,
            clusters,
          }));

        // Build treatment payload
        const treatmentPayload = (config?.treatment || []).map(t => ({
          step: t.step,
          type: t.type,
          description: t.description,
          rate: t.rate,
          conc: t.conc,
          clean_step: t.clean_step,
          slurry_step: t.slurry_step,
          sand_step: t.sand_step,
          start_conc: t.start_conc ?? null,
          end_conc: t.end_conc ?? null,
        }));

        const deployRes = await fetch(`${backendUrl}/wells/${realId}/deploy-config`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            well_metadata: {
              afe: well.afe || null,
              tvd: well.tvd,
              td: well.td,
              curve_start: well.curveStart,
              curve_end: well.curveEnd,
              max_psi: well.maxPsi,
            },
            stage_count: well.stages,
            perforations: perfsPayload,
            treatment_steps: treatmentPayload,
          }),
        });
        if (!deployRes.ok) {
          const err = await deployRes.json().catch(() => ({}));
          throw new Error(err.error || `Failed to deploy config for ${well.name}`);
        }

        // 3b. Save casing strings
        if (config?.casingStrings && config.casingStrings.length > 0) {
          progress(`Saving ${well.name} casing strings...`);
          const casingPayload = config.casingStrings.map((c, i) => ({
            pipe_type: c.type,
            pipe_od: c.pipeOD,
            pipe_id: c.pipeID,
            weight: c.weight,
            grade: c.grade,
            top_depth: c.topDepth,
            bottom_depth: c.bottomDepth,
            capacity_factor: c.capacityFactor,
            effective_length: c.effectiveLength,
            volume: c.volume,
            sort_order: i,
          }));

          const casingRes = await fetch(`${backendUrl}/wells/${realId}/casing-strings`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ strings: casingPayload }),
          });
          if (!casingRes.ok) {
            console.warn(`Failed to save casing strings for ${well.name}, continuing...`);
          }
        }

        // 3c. Upload survey file (only if it's a real user-uploaded file, not a DB placeholder)
        if (survey?.file && survey.file.size > 0) {
          progress(`Uploading ${well.name} survey...`);
          const formData = new FormData();
          formData.append('file', survey.file);

          const surveyRes = await fetch(`${backendUrl}/wells/${realId}/survey/upload`, {
            method: 'POST',
            headers: getAuthHeadersMultipart(),
            body: formData,
          });
          if (!surveyRes.ok) {
            console.warn(`Failed to upload survey for ${well.name}, continuing...`);
          }
        }
      }

      // ── 4. Delete surveys removed by user ────────────────
      const { deletedSurveyIds } = stateRef.current;
      if (deletedSurveyIds.length > 0) {
        progress('Removing deleted surveys...');
        await Promise.all(
          deletedSurveyIds.map(wellId =>
            fetch(`${backendUrl}/wells/${wellId}/survey`, { method: 'DELETE', headers })
              .catch(err => console.warn(`Failed to delete survey for well ${wellId}:`, err))
          )
        );
        // Clear the deletion queue now they're committed to DB
        setState(prev => ({ ...prev, deletedSurveyIds: [] }));
      }

      // ── 5. Update pad with schedule + counts ─────────────
      progress('Updating schedule...');
      const totalStages = wells.reduce((s, w) => s + w.stages, 0);

      await fetch(`${backendUrl}/pads/${currentPadId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          well_count:              wells.length,
          stage_count:             totalStages,
          status:                  'Planned',
          customer_start_ts:       schedule.startDate ? schedule.startDate.getTime() : null,
          customer_end_ts:         schedule.endDate   ? schedule.endDate.getTime()   : null,
          expected_stages_per_day: schedule.expectedStagesPerDay ?? 0,
        }),
      });

      setDeployProgress(prev => ({
        ...prev,
        status: 'success',
        currentStep: 'Deploy complete!',
        completedSteps: [...prev.completedSteps, prev.currentStep],
      }));

      return { success: true };

    } catch (err: any) {
      const errorMsg = err?.message || 'Deploy failed';
      console.error('Deploy failed:', err);
      setDeployProgress(prev => ({
        ...prev,
        status: 'error',
        error: errorMsg,
      }));
      return { success: false, error: errorMsg };
    }
  }, []); // stable — reads state via stateRef, never in deps

  const validateStep = useCallback((step: number): boolean => {
    const { padData, wells, schedule } = stateRef.current;
    switch (step) {
      case 0: return !!(padData.company && padData.padName);
      case 1: return wells.length > 0;
      case 2: return !!(schedule.startDate && schedule.endDate);
      case 3: return true;
      default: return false;
    }
  }, []); // stable

  const goToNextStep = useCallback(() => {
    const currentStep = stateRef.current.currentStep;
    if (currentStep < 3) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else {
      onCompleteRef.current?.(stateRef.current);
    }
  }, []); // stable

  const goToPreviousStep = useCallback(() => {
    setState(prev =>
      prev.currentStep > 0 ? { ...prev, currentStep: prev.currentStep - 1 } : prev
    );
  }, []); // stable

  // All callbacks are stable (empty deps or []) so this useMemo only fires
  // when state actually changes — consumers don't re-render spuriously.
  const value = useMemo<WorkflowContextValue>(() => ({
    state,
    setCurrentStep, updatePadData, setWells, addWell, updateWell,
    removeWell, updateSchedule, setUploadedFile, resetWorkflow,
    validateStep, goToNextStep, goToPreviousStep, loadPadFromApi,
    updateWellConfig, setUploadedSurvey,
    deployWorkflow, deployProgress,
  }), [state, deployProgress]); // callbacks all stable — only state and deployProgress change

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = (): WorkflowContextValue => {
  const context = useContext(WorkflowContext);
  if (!context) throw new Error('useWorkflow must be used within a WorkflowProvider');
  return context;
};

// Constants
export const WORKFLOW_STEPS = [
  { id: 0, title: 'Create Pad', icon: '📦', desc: 'Initialize new pad/well configuration' },
  { id: 1, title: 'Configure Wells', icon: '⚙️', desc: 'Upload template or manual entry' },
  { id: 2, title: 'Schedule Job', icon: '📅', desc: 'Select dates from Frac Calendar' },
  { id: 3, title: 'Review & Deploy', icon: '🚀', desc: 'Confirm and activate job' },
];

export const STATES = [
  'Texas', 'New Mexico', 'Oklahoma', 'Colorado', 'North Dakota',
  'Wyoming', 'Louisiana', 'Pennsylvania', 'Ohio', 'West Virginia',
];

export const DISTRICTS = [
  'Midland', 'Pleasanton', 'Northeast', 'Vernal', 'Longview',
];

export const FORMATIONS = [
  'LSSS JoMill', 'Wolfcamp A', 'Wolfcamp B', 'Bone Spring', 'Spraberry',
  'Delaware Basin', 'Midland Basin', 'Bakken', 'Marcellus', 'Eagle Ford',
];

export default WorkflowContext;